import { BarChart, Bar, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { ChartGrid, ChartTooltipBox, ChartXAxis, ChartYAxis } from '../chart-primitives'

interface Props {
 hourCounts: Record<string, number>
 /** ResponsiveContainer height — a px number, or "100%" to fill a sized parent. */
 height?: number | `${number}%`
}

interface TooltipPayload {
 active?: boolean
 payload?: Array<{ value: number }>
 label?: string
}

function PeakHoursTooltip({ active, payload, label }: TooltipPayload) {
 if (!active || !payload?.length) return null
 const hour = parseInt(label ?? '0')
 const period = hour < 12 ? 'AM' : 'PM'
 const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
 return (
  <ChartTooltipBox>
   <p className="text-foreground">{h12}:00 {period}</p>
   <p className="text-foreground">{payload[0]!.value} sessions</p>
  </ChartTooltipBox>
 )
}

export function PeakHoursChart({ hourCounts, height = 160 }: Props) {
 const data = Array.from({ length: 24 }, (_, i) => ({
  hour: String(i),
  count: hourCounts[String(i)] ?? 0,
 }))

 const sorted = [...data].sort((a, b) => b.count - a.count)
 const top3Hours = new Set(sorted.slice(0, 3).map((d) => d.hour))

 const topFill = 'var(--chart-1)'
 const normalFill = 'rgb(from var(--chart-1) r g b / 0.35)'
 const strokeColor = 'rgba(255,255,255,0.15)'

 return (
  // Fills the height of its (sized) parent when height="100%"; the "top 3 peak
  // hours highlighted" note now lives in the card title's info affordance, so
  // the chart owns the whole card body instead of leaving a caption footer.
  <ResponsiveContainer width="100%" height={height}>
   <BarChart data={data} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
     <ChartGrid vertical={false} />
     <ChartXAxis
      dataKey="hour"
      tickFormatter={(v: string) => {
       const h = parseInt(v)
       if (h === 0) return '12a'
       if (h === 12) return '12p'
       if (h < 12) return `${h}a`
       return `${h - 12}p`
      }}
     />
     <ChartYAxis hide />
     <Tooltip content={<PeakHoursTooltip />} cursor={{ fill: 'rgba(79,70,229,0.08)' }} />
     <Bar dataKey="count" radius={[2, 2, 0, 0]} maxBarSize={14} stroke={strokeColor} strokeWidth={0.75} isAnimationActive={false}>
      {data.map((d) => (
       <Cell key={d.hour} fill={top3Hours.has(d.hour) ? topFill : normalFill} />
      ))}
     </Bar>
    </BarChart>
  </ResponsiveContainer>
 )
}
